import { AUTH_LOGOUT_URL } from '@/config/auth'
import { WEBSITE_URL } from '@/config/system'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Separator } from '@/shared/components/ui/separator'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { clearLastPlaybookQuery } from '@/shared/utils/lastPlaybookQuery'
import { isFreeOrProPlan } from '@/shared/utils/subscriptionPlan'
import { IUser } from '@/types/user'

import AddOutlined from '@mui/icons-material/AddOutlined'
import CheckOutlined from '@mui/icons-material/CheckOutlined'
import ExitToAppOutlined from '@mui/icons-material/ExitToAppOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import React, { ComponentProps } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as CookieConsent from 'vanilla-cookieconsent'
import { cn } from '../lib/utils'

import UserAvatar from './UserAvatar'

type UserMenuProps = {
  user?: IUser
  size?: 'default' | 'sm'
  variant?: 'icon' | 'icon-text'
} & ComponentProps<'div'>

export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  size = 'default',
  className,
  variant = 'icon',
}) => {
  const { workspaceId: currentWorkspaceId, setWorkspaceId } =
    useWorkspaceContext()
  const navigate = useNavigate()

  const { workspaces = [] } = user
  const currentWorkspace = workspaces.find(
    (workspace) => workspace._id === currentWorkspaceId
  )

  const userInitials = user ? `${user.firstName.charAt(0)}` : 'U'
  const {
    flags: [
      isWorkspaceSwitchingEnabled,
      isWorkspaceCreationEnabled,
      isWorkspacePlansEnabled,
    ],
  } = useFeatureFlagsWithWorkspace([
    'workspaceSwitch',
    'workspaceCreation',
    'settingsWorkspacePlans',
  ])

  const userEmail = user?.email

  const avatarSize = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10'
  const buttonSize = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10'

  const handleWorkspaceSwitch = (workspaceId: string) => {
    if (workspaceId === currentWorkspaceId) {
      return
    }

    setWorkspaceId(workspaceId)
    navigate('/')
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-2">
          <button
            className={`flex ${buttonSize} shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-gray-300 transition-colors hover:opacity-80 hover:outline-primary focus-visible:outline-2 focus-visible:outline-primary`}
          >
            <UserAvatar
              email={userEmail}
              initials={userInitials}
              className={avatarSize}
            />
          </button>
          {variant === 'icon-text' && (
            <div className="flex flex-col break-all">
              <span className="text-sm font-medium">
                {[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
              </span>
              <span className="text-xs text-gray-400">{userEmail}</span>
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="-mt-3 mr-4 w-72 rounded-xl bg-white p-3 py-4 drop-shadow-menu"
        align="start"
      >
        <div className={cn('flex flex-col gap-2', className)}>
          {isWorkspaceSwitchingEnabled && workspaces.length > 0 ? (
            workspaces
              .filter((workspace) => !!workspace.name)
              .map((workspace) => (
                <Button
                  key={workspace._id}
                  variant="ghost"
                  size="sm"
                  className="flex w-full items-center justify-between gap-3 rounded-lg"
                  onClick={() => handleWorkspaceSwitch(workspace._id)}
                >
                  <span className="flex items-center gap-3 overflow-hidden">
                    <UserAvatar
                      email={userEmail}
                      initials={workspace.name.charAt(0).toUpperCase()}
                      className="h-6 w-6 shrink-0 text-base"
                    />
                    <span className="flex flex-col items-start">
                      <span className="text-sm font-medium">
                        {workspace.name}
                      </span>
                      <span className="whitespace-pre-wrap break-all text-left text-xs text-gray-500">
                        {workspace.company || userEmail}
                      </span>
                    </span>
                  </span>
                  {currentWorkspaceId === workspace._id && (
                    <CheckOutlined
                      sx={{ fontSize: 20 }}
                      className="text-black"
                    />
                  )}
                </Button>
              ))
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="flex w-full items-center justify-between gap-3 rounded-lg"
              aria-current="true"
              tabIndex={-1}
            >
              <span className="flex items-center gap-3 overflow-hidden">
                <UserAvatar
                  email={userEmail}
                  initials={userInitials}
                  className="h-6 w-6 shrink-0 text-base"
                />
                <span className="flex flex-col items-start">
                  <span className="text-sm font-medium">Personal account</span>
                  <span className="whitespace-pre-wrap break-all text-left text-xs text-gray-500">
                    {userEmail}
                  </span>
                </span>
              </span>
              <CheckOutlined sx={{ fontSize: 20 }} className="text-black" />
            </Button>
          )}

          <Separator />

          {isWorkspaceCreationEnabled && (
            <Button
              disabled
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3"
            >
              <AddOutlined sx={{ fontSize: 24 }} />
              <span className="text-sm">Create a new workspace</span>
            </Button>
          )}
          {isWorkspacePlansEnabled &&
            currentWorkspace &&
            isFreeOrProPlan(
              currentWorkspace?.subscription?.subscriptionPlan?.type
            ) && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-3"
                asChild
              >
                <Link to={window.location.pathname + '?settings=plans'}>
                  <>
                    <OpenInNewOutlined sx={{ fontSize: 24 }} />
                    <span className="text-sm">Upgrade plan</span>
                  </>
                </Link>
              </Button>
            )}
          <Button
            disabled
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-3"
            asChild
          >
            <Link to={window.location.pathname + '?settings'}>
              <SettingsOutlined sx={{ fontSize: 24 }} />
              <span className="text-sm">Settings</span>
            </Link>
          </Button>

          <Separator />

          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              CookieConsent.showPreferences()
            }}
          >
            <InfoOutlined sx={{ fontSize: 24 }} />
            <span className="text-sm">Cookie preferences</span>
          </Button>

          {/* Log out */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              clearLastPlaybookQuery()

              window.location.href =
                AUTH_LOGOUT_URL +
                '?redirectTo=' +
                encodeURIComponent(WEBSITE_URL) +
                '&product=ai-business'
            }}
          >
            <ExitToAppOutlined sx={{ fontSize: 24 }} />
            <span className="text-sm">Log out</span>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
